import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import useNotifications from "@/hooks/useNotifications";
import useTheme from "@/hooks/useTheme";
import { api, relTime } from "@/lib/api";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  House, FolderOpen, FilePlus, ListChecks, Buildings, Receipt, CurrencyInr, Warning,
  MapTrifold, Eye, ShieldCheck, Gear, SignOut, MagnifyingGlass,
  Bell, List, X as XIcon, Star, ChartLine, CreditCard, Code, Sun, Moon, Globe,
  Cube, UserCircle, CaretDown, ChartBar, Camera, MapPinLine, Stamp, Briefcase, Pulse,
  WhatsappLogo, Lightning, Key, Users, UsersThree, Tree, CheckSquare, TrashSimple, SlidersHorizontal,
  ToggleLeft, TreeStructure, Palette, UserPlus, User as UserIcon, PaperPlaneTilt,
  ChatCircle, ChatText, Funnel, Path, Calendar,
} from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";
import { getSidebarForUser, TIER_1_BOTTOM_NAV } from "@/config/sidebar_tiers";
import { TIER_4_GROUPED_SIDEBAR, getInitialGroupState, persistGroupState } from "@/config/sidebar_groups";
import { CaretRight } from "@phosphor-icons/react";

// Map icon name strings (from sidebar_tiers config) → Phosphor components
const ICON_MAP = {
  House, FolderOpen, FilePlus, ListChecks, Buildings, Receipt, CurrencyInr, Warning,
  MapTrifold, Eye, ShieldCheck, Gear, Bell, Star, ChartLine, CreditCard, Code, Globe,
  Cube, UserCircle, ChartBar, Camera, MapPinLine, Stamp, Briefcase, Pulse,
  WhatsappLogo, Lightning, Users, UsersThree, Tree, CheckSquare, TrashSimple, SlidersHorizontal,
  PaperPlaneTilt,
  ChatCircle, ChatText, Funnel, Path, Calendar,
  Toggle: ToggleLeft,
  TreeStructure, Palette, UserPlus,
  User: UserIcon,
};

const NAV_GATES = {
  "/dashboard":  null,
  "/cases":      ["Operations", "Investigations", "FieldOps", "Quality", "Finance", "Legal", "Intelligence"],
  "/clients":    ["Operations", "Investigations", "Finance", "Legal"],
  "/contractors": ["Finance", "Executive"],
  "/products":   ["Operations", "Investigations", "Quality", "Legal"],
  "/parties":    ["Operations", "Investigations", "Legal", "Intelligence"],
  "/invoices":   ["Operations", "Finance"],
  "/payables":   ["Finance"],
  "/escalations":["Operations", "Investigations", "Finance", "Legal"],
  "/leads":      ["Operations", "Finance"],
  "/field-ops":  ["Operations", "Investigations", "FieldOps"],
  "/intelligence":["Intelligence", "Operations", "Investigations"],
  "/network":    ["Operations", "Investigations", "Intelligence", "Legal"],
  "/quality":    ["Quality", "Operations", "Investigations"],
  "/gov-verify": ["Operations", "Investigations", "Legal", "Quality"],
  "/analytics":  ["Finance", "Operations"],
  "/map":        null,
  "/reports":    null,
  "/capture":    ["Operations", "Investigations", "FieldOps"],
  "/whatsapp":   ["Operations", "Investigations", "FieldOps"],
  "/webhooks":   ["Executive", "Operations"],
  "/billing":    ["Finance"],
  "/audit":      [],
  "/admin/build-status": ["Executive"],
  "/settings/team": null,
  "/org": null,
  "/developer":  [],
  "/settings":   [],
};

const NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: House },
  { to: "/cases", label: "Cases", Icon: FolderOpen },
  { to: "/cases/tracker", label: "Tracker", Icon: ListChecks },
  { to: "/reports/sighting", label: "Sightings", Icon: Camera },
  { to: "/cases/nfor", label: "+ New NFOR", Icon: FilePlus },
  { to: "/clients", label: "Clients", Icon: Buildings },
  { to: "/contractors", label: "Suppliers", Icon: Briefcase },
  { to: "/products", label: "Products", Icon: Cube },
  { to: "/parties", label: "Parties", Icon: UserCircle },
  { to: "/invoices", label: "Invoices", Icon: Receipt },
  { to: "/payables", label: "Payables", Icon: CurrencyInr },
  { to: "/escalations", label: "Escalations", Icon: Warning },
  { to: "/leads", label: "Leads", Icon: Star },
  { to: "/field-ops", label: "Field Ops", Icon: MapTrifold },
  { to: "/capture", label: "Capture", Icon: Camera },
  { to: "/whatsapp", label: "WhatsApp", Icon: WhatsappLogo },
  { to: "/map", label: "Map", Icon: MapPinLine },
  { to: "/intelligence", label: "Intelligence", Icon: Eye },
  { to: "/network", label: "Network", Icon: Globe },
  { to: "/quality", label: "Quality", Icon: ShieldCheck },
  { to: "/gov-verify", label: "Gov Verify", Icon: Stamp },
  { to: "/legal-templates", label: "Legal Templates", Icon: ShieldCheck },
  { to: "/admin/tap2verify", label: "Tap2Verify", Icon: ShieldCheck },
  { to: "/analytics", label: "Analytics", Icon: ChartLine },
  { to: "/reports", label: "Reports", Icon: ChartBar },
  { to: "/billing", label: "Billing", Icon: CreditCard },
  { to: "/developer", label: "Developer", Icon: Code },
  { to: "/webhooks", label: "Webhooks", Icon: Lightning },
  { to: "/audit", label: "Audit log", Icon: ShieldCheck },
  { to: "/admin/build-status", label: "Build Status", Icon: Pulse },
  { to: "/settings/team", label: "Team", Icon: Users },
  { to: "/org", label: "Org Chart", Icon: Tree },
  { to: "/settings", label: "Settings", Icon: Gear },
];

function canAccess(user, to) {
  if (!user || user === false) return false;
  if (user.role === "CEO" || user.department === "Executive" || user.department === "Admin") return true;
  const allow = NAV_GATES[to];
  if (allow === null || allow === undefined) return true;
  if (!allow || allow.length === 0) return false;
  return allow.includes(user.department);
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const notifs = useNotifications();
  // UX simplification (June 2026): tier-aware sidebar. Tier 4 / CEO falls back to the legacy full NAV.
  const tierSidebar = getSidebarForUser(user);
  const fullVisibleNav = NAV.filter(n => canAccess(user, n.to));
  const visibleNav = tierSidebar
    ? tierSidebar.map(item => ({ to: item.to, label: item.label, Icon: ICON_MAP[item.icon] || House }))
    : fullVisibleNav;
  const isTier1 = user && user.ux_tier === 1;
  // Tier 4 (COO) and Tier 5 (CEO) get the new collapsible grouped sidebar.
  const useGroupedSidebar = user && (user.ux_tier === 4 || user.ux_tier === 5);

  // Force the password rotation modal open on first sign-in for seeded accounts.
  const forcedRotation = !!(user && user.user_id && user.must_change_password);

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      {/* Mobile overlay */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} className="fixed inset-0 bg-black/40 z-30 lg:hidden"></div>}

      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        logout={async () => { await logout(); nav("/login"); }}
        visibleNav={visibleNav}
        useGroupedSidebar={useGroupedSidebar}
        onChangePassword={() => setPwOpen(true)}
      />

      <main className="flex-1 min-w-0">
        <header className="surface border-b flex items-center justify-between h-14 px-4 lg:px-6 sticky top-0 z-20" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(true)}>
              <List size={18} />
            </button>
            <div className="hidden md:flex items-center gap-2 text-[12px] text-[var(--span-text-2)]">
              <span className="label-eyebrow">SPAN Framework™</span>
              <span className="text-[var(--span-border-strong)]">/</span>
              <span className="font-display font-medium text-[var(--span-text)]">Brand Protection Suite</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GlobalSearch />
            <div className="hidden md:flex items-center gap-2 text-[11px] text-[var(--span-text-2)] font-mono">
              <span className="w-1.5 h-1.5 bg-[var(--span-success)] rounded-full"></span>
              <span>SYS · OPERATIONAL</span>
            </div>
            <LangSwitch />
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="w-9 h-9 hairline flex items-center justify-center surface"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button
              data-testid="notif-bell"
              onClick={() => setNotifOpen(true)}
              className="relative w-9 h-9 hairline flex items-center justify-center surface"
            >
              <Bell size={15} weight={notifs.unread > 0 ? "fill" : "regular"} />
              {notifs.unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[var(--span-danger)] text-white text-[9px] font-mono font-bold flex items-center justify-center rounded-full">
                  {notifs.unread > 99 ? "99+" : notifs.unread}
                </span>
              )}
            </button>
          </div>
        </header>
        <div className={`p-4 lg:p-6 ${isTier1 ? "pb-24 lg:pb-6" : ""}`}>
          <Outlet />
        </div>
      </main>

      {isTier1 && <Tier1BottomNav />}
      {notifOpen && <NotifDrawer onClose={() => setNotifOpen(false)} notifs={notifs} />}
      <ChangePasswordModal
        open={forcedRotation || pwOpen}
        forced={forcedRotation}
        onClose={() => setPwOpen(false)}
      />
    </div>
  );
}

function Sidebar({ mobileOpen, onClose, user, logout, visibleNav, useGroupedSidebar, onChangePassword }) {
  return (
    <aside
      className={`sidebar-dark w-[230px] shrink-0 flex-col fixed lg:static inset-y-0 left-0 z-40 transition-transform duration-200 ${
        mobileOpen ? "flex translate-x-0" : "-translate-x-full lg:translate-x-0 lg:flex hidden lg:flex"
      }`}
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between">
        <img
          src="/api/static/brand/C2_Sidebar_Platform.svg"
          alt="SPAN CasePrime"
          className="h-10 w-auto max-w-[180px] object-contain shrink-0"
          style={{ width: "180px", height: "40px" }}
          data-testid="brand-logo-sidebar"
        />
        <button className="lg:hidden text-white/70 shrink-0" onClick={onClose}><XIcon size={16}/></button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {useGroupedSidebar
          ? <GroupedNav onClose={onClose} />
          : visibleNav.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2 text-[13px] transition-colors border-l-2 ${
                    isActive ? "bg-white/5 border-white text-white" : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={15} weight="regular" />
                <span>{label}</span>
              </NavLink>
            ))
        }
      </nav>

      <div className="px-5 py-4 border-t border-white/10">
        {user && user.user_id && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-white/10 flex items-center justify-center font-mono text-[11px]" data-testid="sidebar-user-initials">
                {(user.initials || user.name || user.email || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="overflow-hidden flex-1 min-w-0">
                <div className="text-[12px] font-medium truncate flex items-center gap-1.5">
                  <span className="truncate">{user.name || user.email}</span>
                  {user.rm_md === "MD" && (
                    <span data-testid="sidebar-badge-md" className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-amber-500/20 text-amber-300 border border-amber-500/30 shrink-0">MD</span>
                  )}
                  {user.rm_md === "RM" && (
                    <span data-testid="sidebar-badge-rm" className="font-mono text-[8px] tracking-[0.18em] px-1.5 py-px bg-sky-500/20 text-sky-300 border border-sky-500/30 shrink-0">RM</span>
                  )}
                </div>
                <div className="text-[10px] text-white/50 truncate">{user.role} · {user.department}</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                data-testid="change-password-btn"
                onClick={() => { if (onChangePassword) onChangePassword(); onClose && onClose(); }}
                className="text-[11px] text-white/60 hover:text-white flex items-center gap-1.5"
              >
                <Key size={12} /> Change password
              </button>
              <button data-testid="logout-btn" onClick={logout} className="text-[11px] text-white/60 hover:text-white flex items-center gap-1.5">
                <SignOut size={12} /> Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function GroupedNav({ onClose }) {
  const [open, setOpen] = useState(() => getInitialGroupState());
  const toggle = (id) => {
    setOpen(prev => {
      const next = { ...prev, [id]: !prev[id] };
      persistGroupState(next);
      return next;
    });
  };
  return (
    <div className="px-1" data-testid="grouped-sidebar">
      {TIER_4_GROUPED_SIDEBAR.map(group => {
        const isOpen = open[group.id];
        return (
          <div key={group.id} className="mb-1">
            <button
              type="button"
              onClick={() => toggle(group.id)}
              data-testid={`sidebar-group-${group.id}`}
              className="w-full flex items-center justify-between px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-mono text-white/40 hover:text-white/70"
            >
              <span>{group.label}</span>
              {isOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
            </button>
            {isOpen && group.items.map(item => {
              const Icon = ICON_MAP[item.icon] || House;
              const testId = `nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
              return (
                <NavLink
                  key={item.to + item.label}
                  to={item.to}
                  onClick={onClose}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 pl-7 pr-4 py-1.5 text-[12.5px] transition-colors border-l-2 ${
                      isActive ? "bg-white/5 border-white text-white" : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
                    }`
                  }
                >
                  <Icon size={14} weight="regular" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge && (
                    <span className="font-mono text-[8px] tracking-wider px-1.5 py-px bg-[var(--span-accent,#004EEB)]/30 text-[var(--span-accent,#004EEB)] border border-[var(--span-accent,#004EEB)]/40">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function Tier1BottomNav() {
  return (
    <nav
      data-testid="tier1-bottom-nav"
      className="fixed bottom-0 inset-x-0 lg:hidden z-40 bg-[var(--span-surface)] border-t border-[var(--span-border)] flex items-stretch justify-around shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {TIER_1_BOTTOM_NAV.map((item) => {
        const Icon = ICON_MAP[item.icon] || House;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`bnav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 py-2 min-h-[56px] text-[10px] uppercase tracking-[0.1em] ${
                isActive ? "text-[var(--span-text)]" : "text-[var(--span-text-2)]"
              }`
            }
          >
            <Icon size={18} weight="regular" />
            <span className="leading-none">{item.label.split(" ")[0]}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="hairline flex items-center gap-2 px-3 h-9 surface w-[260px] hidden md:flex">
        <MagnifyingGlass size={14} className="text-[var(--span-text-2)]" />
        <input
          data-testid="global-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder="Search · ⌘K for everything"
          className="bg-transparent text-[13px] outline-none w-full"
        />
        <kbd className="text-[9px] font-mono uppercase tracking-wider text-[var(--span-text-2)] hairline px-1.5 py-0.5">⌘K</kbd>
      </div>
      {open && results && (
        <div className="absolute right-0 mt-2 w-[420px] card-flat shadow-lg z-50" data-testid="search-results">
          <SearchSection title="Cases" items={results.cases.map(c => ({ key: c.case_id, to: `/cases/${c.case_id}`, primary: c.case_code, secondary: `${c.title} · ${c.client_name}`, badge: c.status }))} onClose={() => setOpen(false)} />
          <SearchSection title="Clients" items={results.clients.map(c => ({ key: c.client_id, to: `/clients`, primary: c.name, secondary: `${c.country_code} · ${c.industry}` }))} onClose={() => setOpen(false)} />
          <SearchSection title="Products" items={(results.products || []).map(p => ({ key: p.product_id, to: `/products`, primary: p.name, secondary: `${p.sku || "—"} · ${p.category || ""}` }))} onClose={() => setOpen(false)} />
          <SearchSection title="Parties" items={(results.parties || []).map(p => ({ key: p.party_id, to: `/parties`, primary: p.name, secondary: `${p.type} · ${p.city || ""} ${p.country_code}` }))} onClose={() => setOpen(false)} />
          <SearchSection title="Invoices" items={results.invoices.map(i => ({ key: i.invoice_id, to: `/invoices`, primary: i.invoice_number, secondary: `${i.currency} ${i.total?.toLocaleString()} · ${i.status}` }))} onClose={() => setOpen(false)} />
          {results.cases.length === 0 && results.clients.length === 0 && results.invoices.length === 0 && (results.products || []).length === 0 && (results.parties || []).length === 0 && (
            <div className="p-4 text-[12px] text-[var(--span-text-2)]">No results.</div>
          )}
        </div>
      )}
    </div>
  );
}

function LangSwitch() {
  const { lang, setLang, langs } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switch"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 hairline flex items-center justify-center surface text-[11px] font-mono uppercase"
        aria-label="Language"
      >
        {lang.toUpperCase()}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 card-flat shadow-lg z-50 min-w-[140px]" data-testid="lang-menu">
          {Object.values(langs).map((l) => (
            <button
              key={l.code}
              data-testid={`lang-opt-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[var(--span-surface-2)] ${lang === l.code ? "font-bold" : ""}`}
            >
              {l.label} <span className="text-[10px] font-mono text-[var(--span-text-2)] uppercase">· {l.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchSection({ title, items, onClose }) {
  if (!items.length) return null;
  return (
    <div className="border-b border-[var(--span-border)] last:border-0">
      <div className="px-3 py-2 label-eyebrow border-b border-[var(--span-border)] bg-[var(--span-surface-2)]">{title}</div>
      <ul>
        {items.map((it) => (
          <li key={it.key}>
            <Link to={it.to} onClick={onClose} className="block px-3 py-2 hover:bg-[var(--span-surface-2)]">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12px] font-semibold">{it.primary}</span>
                {it.badge && <span className="badge text-[10px]">{it.badge}</span>}
              </div>
              <div className="text-[11px] text-[var(--span-text-2)] truncate">{it.secondary}</div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NotifDrawer({ onClose, notifs }) {
  return (
    <div className="fixed inset-0 z-50" data-testid="notif-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-[400px] surface border-l flex flex-col" style={{background: 'var(--span-surface)'}}>
        <div className="px-5 py-4 border-b border-[var(--span-border)] flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Notifications</div>
            <div className="font-display font-bold text-[17px]">{notifs.unread} unread</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={notifs.markAllRead} className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--span-text-2)] hover:text-black" data-testid="notif-mark-all">
              Mark all
            </button>
            <button onClick={onClose}><XIcon size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {notifs.items.length === 0 && <div className="p-5 text-[12px] text-[var(--span-text-2)]">No notifications yet.</div>}
          {notifs.items.map((n) => (
            <div key={n.notification_id} className={`px-5 py-3 border-b border-[var(--span-border)] ${notifs.items.indexOf(n) === -1 ? '' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${n.severity === "warning" ? "bg-[var(--span-warning)]" : n.severity === "danger" ? "bg-[var(--span-danger)]" : n.severity === "success" ? "bg-[var(--span-success)]" : "bg-[var(--span-accent)]"}`}></span>
                  <span className="text-[13px] font-medium">{n.title}</span>
                </div>
                <span className="text-[10px] font-mono text-[var(--span-text-2)] shrink-0">{relTime(n.created_at)}</span>
              </div>
              <div className="text-[12px] text-[var(--span-text-2)] mt-1">{n.body}</div>
              {n.link && (
                <Link to={n.link} onClick={() => { notifs.markRead(n.notification_id); onClose(); }} className="text-[11px] font-mono uppercase tracking-[0.18em] mt-2 inline-block underline">
                  Open →
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
